import { create } from 'zustand';
import { onPersistenceNamespaceChange } from '../stores/helpers/persistenceHelpers';
import { getStudioDrafts, listDefinitions, type DefinitionRow } from '../apis';
import type { DraftModelSummary } from '../studioTypes';
import { readReviewQueue, type ReviewQueueData } from '../components/Knowledge/reviewQueue';
import { readHomeQueue, type HomeQueueData } from './homeApi';
import { needsYouNotes, needsYouRows, type NeedsYouTarget } from './needsYou';

/**
 * Action items (#1509): one list of everything waiting on the viewer, composed from queues that exist today and
 * read as the viewer, so nothing appears that they could not already read. Each row opens where its decision is
 * made, and every action there keeps its own permission check (publish, confirm, approve go through today's routes).
 *
 * - Home's Needs you (`/api/home/queue`, needsYou.ts): its definition and lesson rows are left to the two sources
 *   below, which list them one by one, so nothing is counted twice.
 * - Definition drafts (`/api/definitions?status=draft`): the drafts the review gate would let the viewer publish —
 *   the server's `can_review` per row (an org admin's, or the owner's own in a personal workspace).
 * - Studio drafts (`/api/studio/drafts`): model drafts in the viewer's workspace not yet published.
 * - Knowledge › Needs review (`/api/knowledge/review-queue`): proposed relationships when the viewer may confirm
 *   them, lessons the viewer may review (unconfirmed claims — the only confirm path for something said and not
 *   yet checked), and Add to Knowledge requests (the server lists them to admins only). Its definitions are the
 *   source above.
 *
 * Not listed, for want of a source: metric names with two meanings and agents that hit a limit.
 * A source that could not be read is a note, never an empty list: the count is the rows shown, nothing more.
 */

export type ActionItemSource = 'home' | 'definition' | 'model' | 'knowledge';

export type ActionTarget = NeedsYouTarget
  | { to: 'definition'; mine: boolean }
  | { to: 'draft'; draftId: string }
  | { to: 'knowledge-review' };

export interface ActionItem {
  key: string; source: ActionItemSource; title: string; detail: string | null; action: string; target: ActionTarget;
}

export const SOURCE_LABEL: Record<ActionItemSource, string> = {
  home: 'Needs your say', definition: 'Definitions to publish', model: 'Model drafts', knowledge: 'Knowledge to review',
};

/** What each queue answered; `null` when it could not be read. */
export interface ActionSources {
  home: HomeQueueData | null;
  definitions: DefinitionRow[] | null;
  drafts: DraftModelSummary[] | null;
  review: ReviewQueueData | null;
}

const list = <T,>(v: unknown): T[] => (Array.isArray(v) ? v as T[] : []);
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** A draft definition the viewer can publish, by the review gate's own answer; absent means no (fails closed). */
export function definitionWaits(row: DefinitionRow): boolean {
  return row.status === 'draft' && row.can_review === true;
}

export function actionItems(src: ActionSources): { rows: ActionItem[]; notes: string[] } {
  const rows: ActionItem[] = [];
  const notes: string[] = [];
  const unreadable = (what: string) => notes.push(`${what} could not be read. Try again shortly.`);

  if (src.home) {
    for (const r of needsYouRows(src.home)) {
      if (r.kind === 'definition' || r.kind === 'lesson') continue;
      rows.push({ key: `home:${r.key}`, source: 'home', title: r.title, detail: [r.detail, r.meta].filter(Boolean).join(' · ') || null,
        action: r.action, target: r.target });
    }
    notes.push(...Array.from(new Set(needsYouNotes(src.home))));
  } else unreadable('Home’s queue');

  if (src.definitions) {
    for (const d of list<DefinitionRow>(src.definitions)) {
      if (!definitionWaits(d)) continue;
      rows.push({ key: `definition:${d.id}`, source: 'definition', title: d.display_name || d.name,
        detail: [d.mine ? 'Your draft' : d.author_name ? `Drafted by ${d.author_name}` : 'A colleague’s draft', d.perspective].filter(Boolean).join(' · '),
        action: 'Review', target: { to: 'definition', mine: !!d.mine } });
    }
  } else unreadable('Definition drafts');

  if (src.drafts) {
    for (const d of list<DraftModelSummary>(src.drafts)) {
      if (d.status !== 'draft') continue;
      rows.push({ key: `model:${d.id}`, source: 'model', title: d.name || 'Untitled model',
        detail: [plural(d.metricCount || 0, 'metric'), d.updated_by || d.created_by || null].filter(Boolean).join(' · '),
        action: 'Open draft', target: { to: 'draft', draftId: d.id } });
    }
  } else unreadable('Model drafts');

  if (src.review) {
    const q = src.review;
    if (q.can_confirm) {
      for (const p of list<{ id: string; from_metric?: string; to_metric?: string }>(q.proposals)) {
        rows.push({ key: `proposal:${p.id}`, source: 'knowledge', title: `Proposed link: ${p.from_metric || '?'} → ${p.to_metric || '?'}`,
          detail: 'Confirm or dismiss the relationship', action: 'Review', target: { to: 'knowledge-review' } });
      }
    }
    for (const l of list<ReviewQueueData['lessons'][number]>(q.lessons)) {
      if (!l.can_review) continue;
      rows.push({ key: `lesson:${l.kind}:${l.id}`, source: 'knowledge', title: l.text,
        detail: ['Not yet checked against data', l.who, l.source].filter(Boolean).join(' · '), action: 'Confirm', target: { to: 'knowledge-review' } });
    }
    for (const u of list<ReviewQueueData['uploads'][number]>(q.uploads)) {
      rows.push({ key: `upload:${u.upload_id}`, source: 'knowledge', title: `Add to Knowledge: ${u.name}`,
        detail: [u.by, plural(u.numbers || 0, 'number')].filter(Boolean).join(' · '), action: 'Decide', target: { to: 'knowledge-review' } });
    }
    for (const [part, note] of Object.entries(q.notes || {})) {
      if (part !== 'definitions' && note) notes.push(`Knowledge review (${part}): ${note}`);
    }
  } else unreadable('Knowledge review');

  return { rows, notes };
}

async function settle<T>(read: () => Promise<T>): Promise<T | null> {
  try { return await read(); } catch { return null; }
}

/** Every queue at once, as the viewer; each that fails is `null`. */
export async function readActionSources(): Promise<ActionSources> {
  const [home, definitions, drafts, review] = await Promise.all([
    settle(readHomeQueue), settle(() => listDefinitions({ status: 'draft' })), settle(getStudioDrafts), settle(readReviewQueue)]);
  return { home, definitions, drafts, review };
}

export const ACTION_ITEMS_REFRESH_MS = 30_000;

interface ActionItemsState {
  rows: ActionItem[];
  notes: string[];
  /** Knowledge › Needs review's own count, from the same read (null when it could not be read, never a 0 the server did not say) */
  knowledgeCount: number | null;
  loaded: boolean;
  load: (options?: { background?: boolean; ifStale?: boolean }) => Promise<void>;
  reset: () => void;
}

let request = 0;
// one read at a time from this page, so its own slow queue reads never overlap (a pile-up exhausted the server's
// connection pool): a background refresh (timer, focus) that comes while one is running is dropped; any other load
// queues ONE read after it, so a change the viewer just made is always read. A workspace switch keeps the lock: the
// old workspace's read is still running on the server, so the new workspace's first read waits for it (and its
// result is discarded by `request`).
let inFlight: Promise<void> | null = null;
let inFlightId = 0;   // the `request` id of the read in flight, so the read clears only its own lock
let queued: Promise<void> | null = null;
let lastStarted = 0;
/** A focus refresh within this long of the last read is skipped: the timer keeps the list current. */
export const ACTION_ITEMS_FOCUS_MIN_MS = 10_000;

/** The page and the sidebar badge read one list, so the badge is always the number of rows the page shows. */
export const useActionItems = create<ActionItemsState>((set, get) => ({
  rows: [], notes: [], knowledgeCount: null, loaded: false,
  load: (options?: { background?: boolean; ifStale?: boolean }) => {
    if (inFlight) {
      if (options?.background) return inFlight;
      const after = inFlight;
      queued = queued ?? after.then(() => { queued = null; return get().load(); });
      return queued;
    }
    if (options?.ifStale && Date.now() - lastStarted < ACTION_ITEMS_FOCUS_MIN_MS) return Promise.resolve();
    const id = ++request;
    lastStarted = Date.now();
    const run = (async () => {
      try {
        const sources = await readActionSources();
        if (id !== request) return;
        const count = sources.review?.count;
        set({ ...actionItems(sources), knowledgeCount: typeof count === 'number' ? count : null, loaded: true });
      } finally {
        if (inFlightId === id) { inFlight = null; inFlightId = 0; }
      }
    })();
    // the read awaits before its `finally` can run, so the lock is always set before it is cleared
    inFlight = run; inFlightId = id;
    return run;
  },
  reset: () => { request += 1; lastStarted = 0; set({ rows: [], notes: [], knowledgeCount: null, loaded: false }); },
}));

// The list belongs to a workspace: a switch drops it, and a read still in flight from the old one is never applied.
// Nothing is read here: the switch also moves the workspace's connections generation, and the sidebar's refresh
// reads the new workspace's list on that.
onPersistenceNamespaceChange(() => useActionItems.getState().reset());
